import { Table, Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import SupportTitle from '../SupportCommon/SupportTitle';
import styles from './index.module.scss';
import SupportContactUs from '@/components/SupportCommon/SupportContactUs';
import { useGlobal } from '@/utils/useGlobal';
// import Input from '../UI/SearchInput';

type CNPriecData = {
    type: string;
    id: string;
    name: string;
    price: number;
};

type CNPriceCol = {
    title: string;
    dataIndex: string;
    key: string;
}[];

function CnPriceTable({ tableColumn, tableData, className }: { tableColumn: CNPriceCol; tableData: CNPriecData[]; className: string }) {
    const [selectedType, setSelectedType] = useState<string | undefined>(undefined);

    const typeOptions = useMemo(() => {
        const seen = new Set<string>();
        const list: { value: string; label: string }[] = [];
        for (const row of tableData) {
            if (!seen.has(row.type)) {
                seen.add(row.type);
                list.push({ value: row.type, label: row.type });
            }
        }
        return list;
    }, [tableData]);

    const filtered = useMemo(() => {
        if (!selectedType) return tableData;
        return tableData.filter((row) => row.type === selectedType);
    }, [selectedType, tableData]);

    const typeRowSpans = useMemo(() => {
        const spans = new Array(filtered.length).fill(0);
        let i = 0;
        while (i < filtered.length) {
            let j = i;
            while (j < filtered.length && filtered[j].type === filtered[i].type) j++;
            spans[i] = j - i;
            i = j;
        }
        return spans;
    }, [filtered]);

    const columns = tableColumn.map((col) => {
        if (col.dataIndex === 'type') {
            return {
                ...col,
                render: (value: any, _row: any, index: number) => ({
                    children: value,
                    props: { rowSpan: typeRowSpans[index] ?? 0 },
                }),
            };
        }
        return col;
    });

    return (
        <>
            <Select
                style={{ width: 240, marginBottom: 20, height: 38 }}
                placeholder="CNY"
                allowClear
                value={selectedType}
                onChange={(v) => setSelectedType(v)}
                options={typeOptions}
                showSearch
            />
            <Table className={className} columns={columns} dataSource={filtered} pagination={false} bordered={true} />
        </>
    );
}

export default function SupportPolicy({ lang, policyData }: any) {
    const { useRouter } = useGlobal();
    const { locale } = useRouter();
    const [sortIndex, setSortIndex] = useState(0);
    const [navList, setNavList] = useState<string[]>([]);

    const createdTable = (tableColumn?: string[] | CNPriceCol, tableData?: string[][] | CNPriecData[], className = '') => {
        if (!tableColumn || !tableData) return <div></div>;

        if (locale === 'cn' && className === 'table-11-1') {
            return <CnPriceTable tableColumn={tableColumn as CNPriceCol} tableData={tableData as CNPriecData[]} className={className} />;
        }

        const cols = tableColumn as string[];
        const rows = tableData as string[][];

        const columns = cols.map((item) => {
            if (locale === 'sa' && className === 'table-1-2' && item === 'Accessories') {
                return {
                    title: item,
                    dataIndex: item,
                    key: item,
                    render: (_val: any, _row: any, _i: number) => {
                        const obj = {
                            children: _val,
                            props: { rowSpan: 0 },
                        };
                        if (_i === 0) {
                            obj.props.rowSpan = 10;
                        }
                        return obj;
                    },
                };
            }
            if (locale === 'sa' && className === 'table-1-3' && item === 'Products') {
                return {
                    title: item,
                    dataIndex: item,
                    key: item,
                    render: (_val: any, _row: any, _i: number) => {
                        const obj = {
                            children: _val,
                            props: { rowSpan: 0 },
                        };
                        if (_i === 0) {
                            obj.props.rowSpan = 9;
                        }
                        if (_i === 9) {
                            obj.props.rowSpan = 7;
                        }
                        if (_i === 16) {
                            obj.props.rowSpan = 6;
                        }
                        if (_i === 22) {
                            obj.props.rowSpan = 6;
                        }
                        if (_i === 28) {
                            obj.props.rowSpan = 1;
                        }
                        return obj;
                    },
                };
            }
            return {
                title: item,
                dataIndex: item,
                key: item,
            };
        });
        const data = rows.map((item, rowIndex) => {
            const rowData = {} as Record<string, string | number>;
            rowData['key'] = rowIndex;
            cols.map((key, index) => {
                rowData[key] = item[index] || '--';
            });
            return rowData;
        });

        return <Table className={className} columns={columns} dataSource={data} pagination={false} bordered={true} />;
    };

    useEffect(() => {
        const navList = Object.keys(policyData);
        setNavList(navList);
        setSortIndex(0);
    }, [policyData]);

    return (
        <div className={styles.supportPolicy}>
            <div className="policy-title">
                <SupportTitle title={lang.title} lang={lang} />
            </div>
            <div className="policy-content">
                <div className="left-tab">
                    {navList.map((item, index) => (
                        <div className={`tab-item ${sortIndex === index ? 'active' : ''}`} key={index} onClick={() => setSortIndex(index)}>
                            {item}
                        </div>
                    ))}
                </div>
                <div className="right-content">
                    <h3>
                        {sortIndex + 1}.&nbsp;&nbsp;{navList[sortIndex]}
                    </h3>
                    {policyData[navList[sortIndex]]?.map((item: any, index: number) => (
                        <div className="content-box" key={index}>
                            {item.title && (
                                <h5>
                                    {sortIndex + 1}.{index + 1}&nbsp;&nbsp;
                                    {item.title}
                                </h5>
                            )}
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: item.content,
                                }}
                                style={{ color: '#35404e' }}
                            ></div>
                            {createdTable(item.tableColumn, item.tableData, item.className)}
                            {item.tips && (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: item.tips,
                                    }}
                                ></div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <SupportContactUs isShow={true} />
        </div>
    );
}
